import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import EggVisual from "@/components/EggVisual";
import OmniCelebrationOverlay from "@/components/OmniCelebrationOverlay";
import OmniUnlimitedCelebrationOverlay from "@/components/OmniUnlimitedCelebrationOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 15000;

function fmtRemaining(sec) {
  if (sec <= 0) return "READY";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Border/text/chip presets per tier. Used for hatch history rows + legend tiles.
// Visual egg rendering itself lives in <EggVisual/> driven by the backend `theme`.
const RARITY_STYLES = {
  // 🩶 EEE Ash Egg — pity rail. Life Ash dust only, cannot evolve.
  ash_egg:   { ring: "border-[#6B5A47]",    text: "text-[#C9B9A3]",   chip: "bg-[#5A4A3A]/50 text-[#C9B9A3]" },
  // 🟠 EE Ember Egg — low-tier shop rail. Basic pet (5% Uncommon).
  ember_egg: { ring: "border-[#9A3412]",    text: "text-[#FDBA74]",   chip: "bg-[#C2410C]/40 text-[#FDBA74]" },
  common:    { ring: "border-zinc-700",     text: "text-neutral-300", chip: "bg-zinc-800 text-zinc-300" },
  uncommon:  { ring: "border-emerald-700",  text: "text-emerald-300", chip: "bg-emerald-900/50 text-emerald-300" },
  rare:      { ring: "border-sky-600",      text: "text-sky-300",     chip: "bg-sky-900/50 text-sky-300" },
  epic:      { ring: "border-purple-600",   text: "text-purple-300",  chip: "bg-purple-900/50 text-purple-300" },
  legendary: { ring: "border-[#D4AF37]",    text: "text-[#D4AF37]",   chip: "bg-[#D4AF37]/15 text-[#D4AF37]" },
  mythic:    { ring: "border-red-600",      text: "text-red-300",     chip: "bg-red-900/50 text-red-300" },
  exotic:    { ring: "border-cyan-500",     text: "text-cyan-300",    chip: "bg-cyan-900/50 text-cyan-300" },
  omni:      { ring: "border-pink-500",     text: "text-pink-200",    chip: "bg-gradient-to-r from-pink-600/60 via-yellow-500/60 to-cyan-500/60 text-white" },
  omni_unlimited: { ring: "border-[#DC2626]", text: "text-[#D4AF37]",  chip: "bg-gradient-to-r from-purple-700 via-red-600 to-amber-500 text-white" },
  mystery:   { ring: "border-[#D4AF37]",   text: "text-[#FCD34D]",   chip: "bg-[#D4AF37]/20 text-[#FCD34D]" },
};

export default function PhoenixNest() {
  const { user, refreshUser } = useAuth();
  const [eggs, setEggs] = useState({ pending: [], history: [], wallet: { nests: 0, phoenix_feathers: 0, phoenix_tears: 0, phoenix_hearts: 0, phoenix_crowns: 0, flux_fruit: 0, chefs_kisses: 0, life_ash: 0 }, ready_count: 0, incubating_count: 0, free_hatch_threshold_sec: 180 });
  const [store, setStore] = useState({ items: [] });
  const [rarities, setRarities] = useState([]);
  // 🪦 Feb 10, 2026 — Nest inventory (Tomb of Awakening et al.)
  const [inventory, setInventory] = useState({ items: [], streak: {} });
  const [usingItem, setUsingItem] = useState(null);
  const [hatchingId, setHatchingId] = useState(null);
  const [hatchPopup, setHatchPopup] = useState(null);  // last hatch result for animation
  const [omniCelebration, setOmniCelebration] = useState(null);  // SSS Omni full-screen overlay
  const [omniUnlimitedCeleb, setOmniUnlimitedCeleb] = useState(null);  // SSS Omni Unlimited 🐦‍🔥
  const [reveal, setReveal] = useState(null);  // pending Heart reveal
  const [revealBusy, setRevealBusy] = useState(false);
  const [buyingKey, setBuyingKey] = useState(null);
  const [tick, setTick] = useState(0);
  const [evolvingId, setEvolvingId] = useState(null);
  const [converting, setConverting] = useState(false);
  const heartChecked = useRef(false);

  const loadAll = useCallback(async () => {
    if (!user) return;
    try {
      const [e, s, r, inv] = await Promise.allSettled([
        axios.get(`${API}/nest/eggs`),
        axios.get(`${API}/nest/store`),
        axios.get(`${API}/nest/rarities`),
        axios.get(`${API}/nest/inventory`),
      ]);
      if (e.status === "fulfilled") {
        setEggs(e.value.data);
        // 🥚💨 Rotten egg toast — fires once when the server prunes expired Mystery Eggs
        const rotted = Number(e.value.data?.rotten_eggs_pruned || 0);
        if (rotted > 0) {
          toast.error(`🥚💨 ${rotted} Mystery Egg${rotted > 1 ? "s" : ""} went rotten. Move faster next time!`, { duration: 6000 });
        }
      }
      if (s.status === "fulfilled") setStore(s.value.data);
      if (r.status === "fulfilled") setRarities(r.value.data?.rarities || []);
      if (inv.status === "fulfilled") setInventory(inv.value.data || { items: [], streak: {} });
    } catch { /* silent */ }
  }, [user]);

  // Auto-detect pending Heart reveal on mount (so users land back into the picker if they navigated away)
  useEffect(() => {
    if (!user || heartChecked.current) return;
    heartChecked.current = true;
    (async () => {
      try {
        // We don't have a GET for pending — calling /reveal returns the same options
        // without consuming a Heart IF one is pending. But if none is pending and they
        // have no Hearts, it 402s. So only call it when they have boosters.phoenix_hearts > 0
        // OR after they explicitly tap the open-heart button.
        // We don't auto-open here — just listed in the wallet.
      } catch { /* */ }
    })();
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll for ready eggs + tick timer for live countdowns
  useEffect(() => {
    if (!user) return undefined;
    const poll = setInterval(loadAll, POLL_MS);
    const ticker = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(ticker); };
  }, [user, loadAll]);

  async function hatch(egg, useCrown = false) {
    if (hatchingId) return;
    setHatchingId(egg.egg_id);
    try {
      const { data } = await axios.post(`${API}/nest/hatch/${egg.egg_id}`, { use_crown: useCrown });
      const hatched = data?.hatched;
      // 🐦‍🔥 OMNI UNLIMITED — phoenix-flame celebration
      if (hatched?.rarity === "omni_unlimited") {
        setOmniUnlimitedCeleb(hatched);
        await loadAll();
        await refreshUser();
      // 🌈 OMNI takeover — full-screen celebration instead of the regular popup
      } else if (hatched?.rarity === "omni") {
        setOmniCelebration(hatched);
        await loadAll();
        await refreshUser();
      } else {
        setHatchPopup(hatched);
        await loadAll();
        await refreshUser();
        // If a Heart dropped, jump straight to reveal
        if (hatched?.heart_dropped) {
          toast.success("🫀 PHOENIX HEART DROPPED — open it now!", { duration: 6000 });
        } else if (hatched?.crown_dropped) {
          toast.success(`👑 +1 Phoenix Crown! · ${hatched.rarity.toUpperCase()} +${hatched.nests} 🪹`, { duration: 5000 });
        } else {
          const items = (hatched?.bonus_items || []).map((b) =>
            b === "phoenix_feather" ? "🪶 Feather" : b === "phoenix_tear" ? "💧 Tear" : b
          ).join(" + ");
          const crownNote = hatched?.crown_spent ? " · 👑 spent" : "";
          toast.success(`${hatched.rarity.toUpperCase()} hatched — +${hatched.nests} 🪹${items ? " · " + items : ""}${crownNote}`, { duration: 5000 });
        }
        setTimeout(() => setHatchPopup(null), 4500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't hatch.");
    } finally {
      setHatchingId(null);
    }
  }

  async function openHeart() {
    if (revealBusy) return;
    setRevealBusy(true);
    try {
      const { data } = await axios.post(`${API}/nest/heart/reveal`);
      setReveal(data);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open Heart.");
    } finally {
      setRevealBusy(false);
    }
  }

  async function pickHeartOption(key) {
    setRevealBusy(true);
    try {
      const { data } = await axios.post(`${API}/nest/heart/pick`, { pick: key });
      toast.success(`Claimed ${data?.picked?.icon} ${data?.picked?.label}`, { duration: 4000 });
      setReveal(null);
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pick.");
    } finally {
      setRevealBusy(false);
    }
  }

  async function buyItem(item) {
    if (buyingKey) return;
    setBuyingKey(item.key);
    try {
      await axios.post(`${API}/nest/buy`, { item_key: item.key });
      const isOd = item.currency === "od" || !!item.price_od;
      const amount = isOd ? Number(item.price_od || 0) : Number(item.price_nests || 0);
      const glyph  = isOd ? "Ⓞ OD" : "🪹";
      toast.success(`Bought ${item.icon} ${item.name} — −${amount.toLocaleString()} ${glyph}`, { duration: 4000 });
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBuyingKey(null);
    }
  }

  // 🔮 Force-Rotate — 25 Ⓞ, max 10/day (canon Feb 18 2026)
  async function forceRotate() {
    if (buyingKey) return;
    setBuyingKey("__rotate__");
    try {
      const { data } = await axios.post(`${API}/nest/force-rotate`);
      toast.success(
        `🔮 Rotated · −${data.spent_omni_dollars} Ⓞ · ${data.rotate_remaining} rerolls left today`,
        { duration: 5000 }
      );
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rotate the store.");
    } finally {
      setBuyingKey(null);
    }
  }

  // 🎟️ Buy a Bazaar Ticket bundle with Phoenix Tokens
  async function buyBazaarBundle(bundle) {
    if (buyingKey) return;
    setBuyingKey(bundle.key);
    try {
      const { data } = await axios.post(`${API}/nest/buy-bazaar-tickets`, {
        bundle_key: bundle.key,
      });
      toast.success(
        `🎟️ +${data.tickets_awarded} Bazaar Tickets · −${data.phoenix_tokens_spent} 🐦‍🔥`,
        { duration: 5000 }
      );
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBuyingKey(null);
    }
  }

  async function useTear() {
    try {
      const { data } = await axios.post(`${API}/nest/tears/use`);
      toast.success(`💧 Tear used · Daily Challenge auto-complete · +${data.pt_awarded} Phoenix Tokens 🐦‍🔥`, { duration: 5000 });
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't use Tear.");
    }
  }

  // Fail rates mirror backend FLUX_FRUIT_FAIL_RATES — shown in the confirm dialog.
  // 🟠 ember_egg has its own 98% rate (set in nest_service.RARITIES, not the
  // generic table) — included here so the Evolve button activates for Ember Eggs
  // instead of silently blocking with "can't evolve further".
  const FLUX_FRUIT_FAIL_RATES = {
    ember_egg: 98,
    common: 5, uncommon: 10, rare: 15, epic: 25,
    legendary: 45, mythic: 70, exotic: 90,
  };

  async function evolveEgg(egg) {
    const failPct = FLUX_FRUIT_FAIL_RATES[egg.rarity];
    if (failPct == null) {
      toast.error("This egg can't evolve further.");
      return;
    }
    const ok = window.confirm(
      `🌀 Use 1 Flux Fruit on this ${egg.tier_name || egg.rarity_label} egg?\n\n` +
      `Fail chance: ${failPct}% — on fail the fruit is consumed AND the egg's timer resets.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    if (evolvingId) return;
    setEvolvingId(egg.egg_id);
    try {
      const { data } = await axios.post(`${API}/nest/evolve/${egg.egg_id}`);
      if (data.success) {
        toast.success(
          `✨ EVOLVED! ${data.from_rarity.toUpperCase()} → ${data.to_rarity.toUpperCase()} · new timer ${data.new_timer_min}m`,
          { duration: 6000 }
        );
      } else {
        toast.error(
          `💥 Evolution FAILED — Flux Fruit consumed, timer reset to ${data.new_timer_min}m`,
          { duration: 6000 }
        );
      }
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply Flux Fruit.");
    } finally {
      setEvolvingId(null);
    }
  }

  async function convertAsh() {
    const ashAmt = eggs.wallet?.life_ash || 0;
    if (ashAmt < 250) {
      toast.error(`Need 250 💀 Life Ash. You have ${ashAmt}.`);
      return;
    }
    const ok = window.confirm(`Burn 250 💀 Life Ash for a random-tier egg?\nMost likely Common-Uncommon, but with a 0.1% shot at SSS Omni Rainbow 🌈`);
    if (!ok) return;
    setConverting(true);
    try {
      const { data } = await axios.post(`${API}/ash/convert`);
      toast.success(`🔥 Burned 250 💀 — got a ${data.rarity_awarded.toUpperCase()} egg!`, { duration: 6000 });
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed.");
    } finally {
      setConverting(false);
    }
  }

  // 🥚 Mystery Egg: spend the stamped item to crack it open
  async function unlockMystery(egg) {
    const req = egg.unlock_requirement || {};
    const ok = window.confirm(
      `🥚 Crack this Mystery Egg by spending ${req.amount} ${req.label}?\n\n` +
      `Reward is a 50/50 roll:\n` +
      `  • Random Epic+ egg (with 5% bump to Omni Unlimited 🐦‍🔥)\n` +
      `  • OR bundle: +300 🪹 Nests · +50 💀 Life Ash · +1 random booster\n\n` +
      `⏳ Egg expires in ${fmtRemaining(egg.expires_remaining_sec)} — be quick!`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API}/nest/unlock-mystery/${egg.egg_id}`);
      const r = data?.reward || {};
      if (r.kind === "egg") {
        const e = r.awarded_egg || {};
        toast.success(`🥚→✨ Cracked! Got a ${(e.rarity || r.awarded_rarity || "").toUpperCase()} egg`, { duration: 6000 });
      } else {
        toast.success(
          `🥚→💰 Cracked! +${r.nests} 🪹 · +${r.life_ash} 💀 · +1 ${
            r.booster === "phoenix_crowns" ? "👑 Crown" :
            r.booster === "flux_fruit" ? "🌀 Flux Fruit" : "🫀 Heart"
          }`,
          { duration: 6000 }
        );
      }
      await loadAll();
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't unlock.");
    }
  }

  // 🥚 Special tile: Mystery Egg w/ disappearing timer + item-spend CTA
  const renderMysteryEggCard = (egg) => {
    const cfg = RARITY_STYLES.mystery;
    const req = egg.unlock_requirement || {};
    const expSec = Math.max(0, Number(egg.expires_remaining_sec || 0));
    const warning = expSec < 7200; // <2h
    // Look up the user's current balance of the required item
    const wallet = eggs.wallet || {};
    let have = 0;
    if (req.item_key === "phoenix_feathers") have = wallet.phoenix_feathers || 0;
    else if (req.item_key === "phoenix_tears") have = wallet.phoenix_tears || 0;
    else if (req.item_key === "flux_fruit") have = wallet.flux_fruit || 0;
    else if (req.item_key === "chefs_kisses") have = wallet.chefs_kisses || 0;
    else if (req.item_key === "life_ash") have = wallet.life_ash || 0;
    else if (req.item_key === "colored_feathers") have = "any color";
    const canCrack = req.item_key === "colored_feathers"
      ? true  // server validates per-color stock
      : (have >= (req.amount || 0));
    return (
      <div
        key={egg.egg_id}
        data-testid={`egg-${egg.egg_id}`}
        className={`relative bg-[#0F0F0F] border-2 ${cfg.ring} p-4 flex flex-col items-center gap-2 ${warning ? "mystery-egg-pulse" : ""}`}
        style={warning ? { boxShadow: "0 0 16px rgba(220,38,38,0.45)" } : {}}
      >
        <EggVisual
          theme={egg.theme}
          letterGrade={egg.letter_grade || "?"}
          size={56}
          showRank={false}
        />
        {/* Mystery sigil */}
        <div className="absolute top-2 left-2 text-[#D4AF37] text-xs font-bold tracking-widest">
          ?
        </div>
        <div className={`text-[10px] uppercase tracking-widest font-bold ${cfg.text}`}>
          Mystery Egg
        </div>
        <div className={`font-mono tabular-nums text-xs ${warning ? "text-[#DC2626]" : "text-neutral-400"}`}>
          ⏳ {fmtRemaining(expSec)}
        </div>
        <div className="text-[10px] text-center text-neutral-300 leading-tight px-1">
          Need <span className="font-bold text-[#FCD34D]">{req.amount}</span> {req.label}
        </div>
        <div className="text-[9px] text-neutral-500 tabular-nums">
          You have: {req.item_key === "colored_feathers" ? "see Nest" : have}
        </div>
        <button
          data-testid={`mystery-unlock-${egg.egg_id}`}
          onClick={() => unlockMystery(egg)}
          disabled={!canCrack}
          className={`w-full mt-1 font-bold uppercase tracking-widest text-[10px] py-1.5 ${
            canCrack
              ? "bg-[#D4AF37] hover:bg-[#F1C84B] text-black"
              : "bg-zinc-800 text-neutral-500 cursor-not-allowed"
          }`}
          title={canCrack ? "Crack it open!" : `Need more ${req.label}`}
        >
          {canCrack ? "Crack 🔓" : "Not Enough"}
        </button>
      </div>
    );
  };

  // Live countdown derived from each egg's hatches_at
  const renderEggCard = (egg) => {
    if (egg.unlock_via_item || egg.rarity === "mystery") {
      return renderMysteryEggCard(egg);
    }
    const cfg = RARITY_STYLES[egg.rarity] || RARITY_STYLES.common;
    let remaining = egg.remaining_sec;
    if (egg.hatches_at) {
      remaining = Math.max(0, Math.floor((new Date(egg.hatches_at) - Date.now()) / 1000));
    }
    const ready = remaining <= 0;
    const freeSkip = !ready && remaining <= (eggs.free_hatch_threshold_sec || 180);
    const canUseCrown = !ready && !freeSkip && (eggs.wallet?.phoenix_crowns || 0) > 0;
    return (
      <div
        key={egg.egg_id}
        data-testid={`egg-${egg.egg_id}`}
        className={`relative bg-[#0F0F0F] border-2 ${cfg.ring} p-4 flex flex-col items-center gap-2`}
      >
        <EggVisual
          theme={egg.theme}
          letterGrade={egg.letter_grade || "E"}
          size={56}
          ready={ready || freeSkip}
        />
        <div className={`text-[10px] uppercase tracking-widest font-bold ${cfg.text}`}>
          {egg.tier_name || egg.rarity_label}
        </div>
        <div className="font-mono tabular-nums text-sm text-white">
          {ready ? "READY" : fmtRemaining(remaining)}
        </div>
        {ready ? (
          <button
            data-testid={`hatch-${egg.egg_id}`}
            onClick={() => hatch(egg, false)}
            disabled={hatchingId === egg.egg_id}
            className="w-full mt-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-[10px] py-1.5 disabled:opacity-60"
          >
            {hatchingId === egg.egg_id ? "Hatching…" : "Hatch 🐣"}
          </button>
        ) : freeSkip ? (
          <button
            data-testid={`hatch-free-${egg.egg_id}`}
            onClick={() => hatch(egg, false)}
            disabled={hatchingId === egg.egg_id}
            className="w-full mt-1 bg-emerald-700 hover:bg-emerald-600 text-white font-bold uppercase tracking-widest text-[10px] py-1.5 disabled:opacity-60"
            title="Within 3 minutes — hatch for free!"
          >
            {hatchingId === egg.egg_id ? "Hatching…" : "Hatch Free ✨"}
          </button>
        ) : canUseCrown ? (
          <button
            data-testid={`hatch-crown-${egg.egg_id}`}
            onClick={() => {
              if (window.confirm(`Spend 1 👑 Phoenix Crown to instant-hatch this ${egg.rarity_label} egg?`)) {
                hatch(egg, true);
              }
            }}
            disabled={hatchingId === egg.egg_id}
            className="w-full mt-1 bg-[#160B22] border border-[#D4AF37] hover:bg-[#D4AF37]/15 text-[#D4AF37] font-bold uppercase tracking-widest text-[10px] py-1.5 disabled:opacity-60"
            title={`Spend 1 Crown (you have ${eggs.wallet?.phoenix_crowns}) to instant-hatch`}
          >
            {hatchingId === egg.egg_id ? "Hatching…" : "Use 👑 Crown"}
          </button>
        ) : (
          <div className="w-full mt-1 text-center text-[10px] text-neutral-500 uppercase tracking-widest">
            +{egg.preview_nests} 🪹
          </div>
        )}
        {/* 🌀 Flux Fruit — only on INCUBATING eggs. Ash Eggs are
            hard-blocked (no_evolve); Omni is top-tier. */}
        {!ready && egg.rarity !== "omni" && egg.rarity !== "ash_egg" && (eggs.wallet?.flux_fruit || 0) > 0 && (
          <button
            data-testid={`evolve-${egg.egg_id}`}
            onClick={() => evolveEgg(egg)}
            disabled={evolvingId === egg.egg_id}
            className="w-full text-center text-[10px] uppercase tracking-widest font-bold py-1 border border-fuchsia-700 bg-fuchsia-900/30 text-fuchsia-200 hover:bg-fuchsia-700/40 disabled:opacity-50"
            title={`Use 1 🌀 Flux Fruit · ${FLUX_FRUIT_FAIL_RATES[egg.rarity] ?? "?"}% fail`}
          >
            {evolvingId === egg.egg_id ? "Evolving…" : `🌀 Evolve (${FLUX_FRUIT_FAIL_RATES[egg.rarity] ?? "?"}% fail)`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="nest-page">
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              🪹 The Phoenix Nest
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">
              Eggs <span className="text-[#D4AF37]">Hatched.</span>
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl">
              Activities drop eggs. Eggs hatch into 🪹 Nests. Spend Nests in the rotating store. Hatch a Legendary for a chance at a 🫀 Phoenix Heart loot box.
            </p>
          </div>
          <Link to="/profile" className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline">
            ← My Profile
          </Link>
        </div>

        {/* Wallet strip */}
        {user && (
          <div data-testid="nest-wallet" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-3 border border-[#D4AF37]/30 bg-gradient-to-br from-[#160B22] to-[#0A0A0A] p-3">
            <WalletStat label="Nests" value={eggs.wallet?.nests || 0} icon="🪹" testId="w-nests" />
            <WalletStat label="Feathers" value={eggs.wallet?.phoenix_feathers || 0} icon="🪶" testId="w-feathers" />
            <WalletStat label="Tears" value={eggs.wallet?.phoenix_tears || 0} icon="💧" testId="w-tears" />
            <WalletStat label="Crowns" value={eggs.wallet?.phoenix_crowns || 0} icon="👑" testId="w-crowns" />
            <WalletStat label="Hearts" value={eggs.wallet?.phoenix_hearts || 0} icon="🫀" testId="w-hearts" />
            <WalletStat label="Flux Fruit" value={eggs.wallet?.flux_fruit || 0} icon="🌀" testId="w-flux-fruit" />
            <WalletStat label="Kisses" value={eggs.wallet?.chefs_kisses || 0} icon="💋" testId="w-kisses" />
            <WalletStat label="Life Ash" value={eggs.wallet?.life_ash || 0} icon="💀" testId="w-ash" />
          </div>
        )}

        {/* 🪦 Nest Inventory strip — Feb 10, 2026 (Tomb of Awakening et al.) */}
        {user && (inventory.items || []).some((it) => (it.count || 0) > 0) && (
          <NestInventoryStrip
            items={inventory.items || []}
            streak={inventory.streak || {}}
            busy={usingItem}
            onUse={async (itemId) => {
              if (usingItem) return;
              setUsingItem(itemId);
              try {
                const res = await axios.post(
                  `${API}/nest/inventory/use`,
                  { item_id: itemId },
                );
                if (res.data?.used) {
                  toast.success(res.data.message || "Item consumed.");
                } else {
                  toast.info(res.data?.message || "The Nest resists this use.");
                }
                await loadAll();
                await refreshUser?.();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not use the item.");
              } finally {
                setUsingItem(null);
              }
            }}
          />
        )}

        {/* 💀 Life Ash → Random Egg conversion panel */}
        {user && (
          <div data-testid="ash-converter" className="mb-8 border border-zinc-800 bg-[#0F0F0F] p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
                💀 Life Ash Converter
              </div>
              <div className="text-xs text-neutral-400">
                Burn <span className="text-white font-mono tabular-nums">250 💀</span> for a random-tier egg.
                Most likely Common, but 0.1% chance of <span className="text-pink-300 font-bold">SSS Omni 🌈</span>.
              </div>
              <div className="mt-1 text-xs">
                <span className="text-neutral-500">You have:</span>{" "}
                <span className="text-white font-mono tabular-nums">{eggs.wallet?.life_ash || 0}</span>{" "}
                <span className="text-neutral-500">💀</span>
              </div>
            </div>
            <button
              data-testid="ash-convert-btn"
              onClick={convertAsh}
              disabled={converting || (eggs.wallet?.life_ash || 0) < 250}
              className="px-5 py-2 bg-gradient-to-r from-fuchsia-700 to-pink-700 hover:from-fuchsia-600 hover:to-pink-600 disabled:from-zinc-800 disabled:to-zinc-800 text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {converting ? "Burning…" : "🔥 Burn 250 → Egg"}
            </button>
          </div>
        )}

        {!user && (
          <div className="bg-[#0F0F0F] border border-zinc-900 p-6 text-center">
            <p className="text-neutral-300">Sign in to see your Nest.</p>
          </div>
        )}

        {/* Egg tray */}
        {user && (
          <div className="mb-10">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                Your Eggs · {eggs.ready_count} ready · {eggs.incubating_count} hatching
              </div>
              {eggs.pending?.length === 0 && (
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Sign in or complete a challenge to earn eggs
                </div>
              )}
            </div>
            {eggs.pending?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="egg-tray">
                {eggs.pending.map(renderEggCard)}
              </div>
            ) : (
              <div className="bg-[#0F0F0F] border border-zinc-900 p-6 text-center text-sm text-neutral-500">
                The nest is quiet. Sign in tomorrow or complete a Daily Challenge to drop new eggs.
              </div>
            )}
          </div>
        )}

        {/* Heart open / pick UI */}
        {user && (eggs.wallet?.phoenix_hearts || 0) > 0 && !reveal && (
          <div data-testid="heart-prompt" className="mb-10 bg-gradient-to-br from-[#160B22] to-[#0A0A0A] border-2 border-[#D4AF37] p-5 text-center">
            <div className="text-5xl mb-2">🫀</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              {eggs.wallet.phoenix_hearts} Phoenix Heart{eggs.wallet.phoenix_hearts > 1 ? "s" : ""} Ready
            </div>
            <p className="text-sm text-neutral-300 mb-4">Open one to reveal 2 random items — you pick one.</p>
            <button
              onClick={openHeart}
              disabled={revealBusy}
              data-testid="heart-open-btn"
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-[0.3em] text-xs py-3 px-8 disabled:opacity-60"
            >
              {revealBusy ? "Opening…" : "Open Heart 🫀"}
            </button>
          </div>
        )}

        {reveal && reveal.options && (
          <div data-testid="heart-reveal-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-6 max-w-2xl w-full">
              <div className="text-center mb-6">
                <div className="text-5xl mb-2 animate-pulse">🫀</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  Phoenix Heart Reveals
                </div>
                <p className="text-sm text-neutral-300 mt-1">Pick ONE — the other vanishes.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {reveal.options.map((opt) => (
                  <button
                    key={opt.key}
                    data-testid={`heart-option-${opt.key}`}
                    onClick={() => pickHeartOption(opt.key)}
                    disabled={revealBusy}
                    className="bg-[#160B22] border-2 border-[#D4AF37] hover:bg-[#D4AF37]/10 p-5 flex flex-col items-center gap-2 transition-colors disabled:opacity-60"
                  >
                    <div className="text-5xl">{opt.icon}</div>
                    <div className="font-display text-xl text-white">{opt.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                      Pick This
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Store */}
        {user && (
          <div className="mb-10">
            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                Today's Nest Store · Rotates Daily at 00:00 PT
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                Limited stock · random discounts
              </div>
            </div>

            {/* 🔮 Force-Rotate — 25 Ⓞ, max 10/day */}
            {store.force_rotate && (
              <div
                data-testid="nest-force-rotate-card"
                className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-purple-700/50 bg-gradient-to-br from-[#160B22] via-[#0F0F0F] to-[#0A0A0A] px-4 py-3"
              >
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-bold mb-1">
                    🔮 Force Rotate
                  </div>
                  <div className="text-xs text-neutral-400 leading-snug">
                    Reroll today&apos;s store + themed eggs. Costs{" "}
                    <span className="text-[#D4AF37] font-bold">{store.force_rotate.cost_omni_dollars} Ⓞ</span>{" "}
                    · <span className="text-purple-300 font-bold">{store.force_rotate.remaining_today}</span> left today
                  </div>
                </div>
                <button
                  onClick={forceRotate}
                  disabled={
                    buyingKey === "__rotate__" ||
                    store.force_rotate.remaining_today <= 0 ||
                    (eggs.wallet?.omni_dollars || store.force_rotate.omni_dollars || 0) < store.force_rotate.cost_omni_dollars
                  }
                  data-testid="nest-force-rotate-btn"
                  className="px-5 py-2 bg-gradient-to-r from-purple-700 to-fuchsia-700 hover:from-purple-600 hover:to-fuchsia-600 text-white font-bold uppercase tracking-widest text-xs disabled:from-zinc-800 disabled:to-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                >
                  {buyingKey === "__rotate__"
                    ? "Rotating…"
                    : store.force_rotate.remaining_today <= 0
                    ? "Cap reached"
                    : `Reroll · ${store.force_rotate.cost_omni_dollars} Ⓞ`}
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nest-store">
              {store.items.map((item) => {
                // 💰 Feb 2026 owner spec (#7 / B2): OD-priced items (Time Skip
                // 395 OD) live in the same rotation. Route the price display +
                // affordability check based on `item.currency`.
                const isOd = item.currency === "od" || !!item.price_od;
                const priceAmount = isOd ? Number(item.price_od || 0) : Number(item.price_nests || 0);
                const priceGlyph  = isOd ? "Ⓞ OD" : "🪹";
                const wallet      = isOd
                  ? Number(eggs.wallet?.omni_dollars || 0)
                  : Number(eggs.wallet?.nests || 0);
                const canAfford = wallet >= priceAmount;
                return (
                  <div
                    key={item.key}
                    data-testid={`store-${item.key}`}
                    className="bg-[#0F0F0F] border border-zinc-900 p-4 flex flex-col items-center gap-2 relative"
                  >
                    {item.discount_pct > 0 && (
                      <div className="absolute top-2 right-2 bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-widest" data-testid={`store-disc-${item.key}`}>
                        -{item.discount_pct}%
                      </div>
                    )}
                    <div className="text-4xl">{item.icon}</div>
                    <div className="font-display text-lg text-white text-center">{item.name}</div>
                    <div className="text-xs text-neutral-400 text-center min-h-[2.5em]">{item.description}</div>
                    <div
                      className="font-mono tabular-nums text-xl text-[#D4AF37] mt-1"
                      data-testid={`store-price-${item.key}`}
                    >
                      {priceAmount.toLocaleString()} {priceGlyph}
                      {!isOd && item.discount_pct > 0 && (
                        <span className="text-xs text-neutral-500 line-through ml-2">{item.base_price_nests}</span>
                      )}
                    </div>
                    <button
                      onClick={() => buyItem(item)}
                      disabled={buyingKey === item.key || !canAfford}
                      data-testid={`buy-${item.key}`}
                      className="w-full mt-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-xs py-2 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                    >
                      {buyingKey === item.key ? "Buying…" : canAfford ? "Buy" : `Need ${(priceAmount - wallet).toLocaleString()} more`}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* 🥚 Themed Eggs — DAILY-ROTATED store section (canon Feb 14 2026:
                only a random subset of the 5 canon themed eggs appears each
                day, with a chance for one to be on sale). */}
            {(store.themed_eggs || []).length > 0 && (
              <div className="mt-8">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    🥚 Themed Eggs · Rotates Daily at 00:00 PT
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Save for tomorrow · watch for a sale
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="themed-egg-store">
                  {(store.themed_eggs || []).map((item) => {
                    const canAfford = (eggs.wallet?.nests || 0) >= item.price_nests;
                    const hrs = Math.round(((item.timer_min || 0) / 60) * 10) / 10;
                    return (
                      <div
                        key={item.key}
                        data-testid={`themed-egg-${item.themed_egg_key}`}
                        className="bg-[#0F0F0F] border border-zinc-900 p-4 flex flex-col items-center gap-2 relative"
                      >
                        {item.discount_pct > 0 && (
                          <div
                            className="absolute top-2 left-2 bg-[#DC2626] text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-widest pointer-events-none"
                            data-testid={`themed-disc-${item.themed_egg_key}`}
                          >
                            -{item.discount_pct}%
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-[#D4AF37]/20 text-[#D4AF37] text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-widest pointer-events-none">
                          {hrs}h hatch
                        </div>
                        <div className="text-4xl">{item.icon}</div>
                        <div className="font-display text-lg text-white text-center">{item.name}</div>
                        <div className="text-xs text-neutral-400 text-center min-h-[3em] leading-snug">
                          {item.description}
                        </div>
                        <div className="font-mono tabular-nums text-xl text-[#D4AF37] mt-1">
                          {item.price_nests} 🪹
                          {item.discount_pct > 0 && (
                            <span className="text-xs text-neutral-500 line-through ml-2">
                              {item.base_price_nests}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => buyItem(item)}
                          disabled={buyingKey === item.key || !canAfford}
                          data-testid={`buy-themed-${item.themed_egg_key}`}
                          className="w-full mt-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-xs py-2 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                        >
                          {buyingKey === item.key
                            ? "Buying…"
                            : canAfford
                            ? "Buy Egg"
                            : `Need ${item.price_nests - (eggs.wallet?.nests || 0)} more`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 🎟️ Bazaar Ticket Bundles — always available (canon Feb 18 2026).
                Paid in 🐦‍🔥 Phoenix Tokens; deliver 🎟️ Bazaar Tokens. */}
            {(store.bazaar_ticket_bundles || []).length > 0 && (
              <div className="mt-8">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    🎟️ Bazaar Ticket Bundles · Paid in 🐦‍🔥 Phoenix Tokens
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Always available
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bazaar-ticket-store">
                  {(store.bazaar_ticket_bundles || []).map((b) => {
                    const have = Number(eggs.wallet?.phoenix_tokens || user?.tokens || 0);
                    const canAfford = have >= b.phoenix_tokens;
                    return (
                      <div
                        key={b.key}
                        data-testid={`bazaar-bundle-${b.key}`}
                        className="bg-[#0F0F0F] border border-amber-800/40 p-4 flex flex-col items-center gap-2"
                      >
                        <div className="text-4xl">{b.icon}</div>
                        <div className="font-display text-lg text-white text-center">{b.label}</div>
                        <div className="text-xs text-neutral-400 text-center min-h-[2.5em] leading-snug">
                          {b.blurb}
                        </div>
                        <div className="font-mono tabular-nums text-2xl text-amber-300">
                          {b.tickets} 🎟️
                        </div>
                        <div className="font-mono tabular-nums text-sm text-[#D4AF37]">
                          {b.phoenix_tokens} 🐦‍🔥
                        </div>
                        <button
                          onClick={() => buyBazaarBundle(b)}
                          disabled={buyingKey === b.key || !canAfford}
                          data-testid={`buy-bazaar-${b.key}`}
                          className="w-full mt-1 bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-widest text-xs py-2 disabled:bg-zinc-800 disabled:text-neutral-500 disabled:cursor-not-allowed"
                        >
                          {buyingKey === b.key
                            ? "Buying…"
                            : canAfford
                            ? `Buy · ${b.phoenix_tokens} 🐦‍🔥`
                            : `Need ${b.phoenix_tokens - have} more 🐦‍🔥`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Consume actions */}
        {user && (eggs.wallet?.phoenix_tears || 0) > 0 && (
          <div data-testid="tear-consume" className="mb-10 bg-[#0F0F0F] border border-zinc-900 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💧</span>
              <div>
                <div className="text-sm text-white font-bold">{eggs.wallet.phoenix_tears} Phoenix Tear{eggs.wallet.phoenix_tears > 1 ? "s" : ""} ready</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Use one to auto-complete today's Daily Challenge</div>
              </div>
            </div>
            <button
              onClick={useTear}
              data-testid="use-tear-btn"
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-black font-bold uppercase tracking-widest text-xs py-2 px-4"
            >
              Use Tear
            </button>
          </div>
        )}

        {/* Rarity legend */}
        {rarities.length > 0 && (
          <div data-testid="rarity-legend" className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
              Egg Rarities · EEE → SSS
            </div>
            {(() => {
              const unlocked = new Set(user?.unlocked_tiers || []);
              const totalTiers = rarities.length;
              const knownCount = rarities.filter((r) => unlocked.has(r.key)).length;
              return (
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3" data-testid="pokedex-count">
                  Discovered <span className="text-[#D4AF37] font-mono tabular-nums">{knownCount}</span> / {totalTiers} · Hatch new tiers to reveal them
                </div>
              );
            })()}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {rarities.map((r) => {
                const cfg = RARITY_STYLES[r.key] || RARITY_STYLES.common;
                const unlockedSet = new Set(user?.unlocked_tiers || []);
                // Themed / partner / holiday eggs are revealed on the
                // roster by default — the owner has already published
                // their canon art. Only standard E→SSS tiers stay
                // silhouetted behind the "hatch to reveal" gate.
                const isLocked = !r.themed && !unlockedSet.has(r.key);
                const isPlaceholder = !!r.placeholder;
                return (
                  <div
                    key={r.key}
                    data-testid={`rarity-tile-${r.key}`}
                    data-locked={isLocked ? "true" : "false"}
                    data-themed={r.themed ? "true" : "false"}
                    className={`bg-[#0F0F0F] border ${isLocked ? "border-zinc-800" : cfg.ring} p-3 flex flex-col items-center gap-1 relative`}
                    title={
                      isLocked
                        ? "??? — Hatch this tier to reveal"
                        : (r.tier_name || r.label) + (isPlaceholder ? " · Coming soon" : "")
                    }
                  >
                    {r.themed && (
                      <div className="absolute top-1 right-1 text-[8px] uppercase tracking-widest font-bold text-[#D4AF37]/70 pointer-events-none">
                        {isPlaceholder ? "SOON" : "NEW"}
                      </div>
                    )}
                    <EggVisual
                      theme={r.theme}
                      letterGrade={r.letter_grade || "E"}
                      size={44}
                      silhouette={isLocked}
                    />
                    <div className={`text-[10px] uppercase tracking-widest font-bold ${isLocked ? "text-neutral-600" : cfg.text} text-center mt-1`}>
                      {isLocked ? "???" : (r.tier_name || r.label)}
                    </div>
                    <div className="font-mono tabular-nums text-xs text-neutral-400">
                      {isLocked ? "—" : (r.timer_min < 60 ? `${r.timer_min}m` : `${r.timer_min / 60}h`)}
                    </div>
                    <div className={`font-mono tabular-nums text-xs font-bold ${isLocked ? "text-neutral-700" : "text-[#D4AF37]"}`}>
                      {isLocked ? "+? 🪹" : `+${r.nests} 🪹`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hatch history */}
        {user && eggs.history?.length > 0 && (
          <div data-testid="hatch-history">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
              Recent Hatches
            </div>
            <div className="space-y-1">
              {eggs.history.slice(0, 10).map((h) => {
                const cfg = RARITY_STYLES[h.rarity] || RARITY_STYLES.common;
                const colorFeather = h.colored_feather_color;
                return (
                  <div key={h.egg_id} className="bg-[#0F0F0F] border border-zinc-900 px-3 py-2 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${cfg.chip}`}>
                        {h.letter_grade ? `${h.letter_grade} · ` : ""}{h.rarity}
                      </span>
                      <span className="font-mono tabular-nums text-[#D4AF37]">+{h.nests} 🪹</span>
                      {h.bonus_items?.length > 0 && (
                        <span className="text-xs text-neutral-400">
                          {h.bonus_items.map((b, i) => {
                            if (b === "phoenix_feather") return <span key={i}>🪶</span>;
                            if (b === "phoenix_tear")    return <span key={i}>💧</span>;
                            if (b === "phoenix_crown")   return <span key={i}>👑</span>;
                            if (b.startsWith("colored_feather_")) return null;
                            return <span key={i}>{b}</span>;
                          })}
                        </span>
                      )}
                      {colorFeather && (
                        <span
                          className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 text-white"
                          style={{ background: `linear-gradient(135deg, #1F0F03, ${colorFeather})` }}
                          title={`Awarded a ${colorFeather} Phoenix Feather`}
                        >
                          🪶 {colorFeather}
                        </span>
                      )}
                      {h.heart_dropped && <span>🫀</span>}
                    </div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
                      {new Date(h.hatched_at).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* 🌈 SSS Omni Rainbow celebration */}
      <OmniCelebrationOverlay
        open={!!omniCelebration}
        hatched={omniCelebration}
        onClose={() => setOmniCelebration(null)}
      />

      {/* 🐦‍🔥 SSS Omni Unlimited celebration */}
      <OmniUnlimitedCelebrationOverlay
        open={!!omniUnlimitedCeleb}
        hatched={omniUnlimitedCeleb}
        onClose={() => setOmniUnlimitedCeleb(null)}
      />

      {/* Hatch celebration popup */}
      {hatchPopup && (
        <div className="fixed inset-x-0 bottom-24 z-40 flex justify-center pointer-events-none">
          <div
            data-testid="hatch-popup"
            className={`tier-up-overlay bg-[#0A0A0A] border-2 px-6 py-4 text-center min-w-[260px] ${RARITY_STYLES[hatchPopup.rarity]?.ring || "border-zinc-700"}`}
          >
            <div className="flex justify-center mb-2">
              <EggVisual
                theme={hatchPopup.theme}
                letterGrade={hatchPopup.letter_grade || "E"}
                size={64}
              />
            </div>
            <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${RARITY_STYLES[hatchPopup.rarity]?.text || "text-white"}`}>
              {hatchPopup.letter_grade ? `${hatchPopup.letter_grade} · ` : ""}{hatchPopup.tier_name || hatchPopup.rarity} Hatched
            </div>
            <div className="text-white font-mono tabular-nums text-lg mt-1">+{hatchPopup.nests} 🪹</div>
            {hatchPopup.colored_feather_color && (
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mt-2">
                🪶 +1 {hatchPopup.colored_feather_color} Feather
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WalletStat({ label, value, icon, testId }) {
  return (
    <div className="bg-[#0A0A0A] border border-zinc-900 p-3 text-center" data-testid={testId}>
      <div className="text-2xl leading-none mb-1">{icon}</div>
      <div className="font-mono tabular-nums text-xl text-white">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

// ─── 🪦 Nest Inventory Strip — Feb 10, 2026 ────────────────
// Renders the player's Phoenix Nest consumable bag. Tomb of Awakening
// is the first citizen — future consumables slot into the same strip.
// Each card exposes a "Use" (or item-specific action) button that is
// gated by the item's precondition (e.g. Tomb requires a lost streak).
function NestInventoryStrip({ items, streak, busy, onUse }) {
  const owned = (items || []).filter((it) => (it.count || 0) > 0);
  if (owned.length === 0) return null;
  return (
    <div
      className="mb-3 border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#160B22]/60 via-[#0A0A0A] to-[#160B22]/60 p-3"
      data-testid="nest-inventory-strip"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🪹</span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Nest Inventory
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {owned.map((it) => (
          <NestInventoryItemCard
            key={it.item_id}
            item={it}
            streak={streak}
            busy={busy === it.item_id}
            onUse={onUse}
          />
        ))}
      </div>
    </div>
  );
}

function NestInventoryItemCard({ item, streak, busy, onUse }) {
  let canUse = true;
  let hint = "";
  if (item.requires === "broken_login_streak") {
    canUse = !!streak?.has_lost_streak;
    hint = canUse
      ? `Rouse the ${streak.lost_amount}-day streak lost ${streak.broken_pt ? "on " + streak.broken_pt : "recently"}.`
      : "The Tomb only stirs when there is a streak to undo.";
  }
  return (
    <div
      className="border border-purple-500/40 bg-black/60 p-3 flex items-start gap-3"
      data-testid={`nest-inv-${item.item_id}`}
    >
      <div className="text-4xl leading-none pt-1">{item.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-white truncate">{item.name}</div>
          <span
            className="text-[10px] font-bold text-emerald-300 bg-emerald-900/50 px-1.5 py-0.5 rounded"
            data-testid={`nest-inv-${item.item_id}-count`}
          >
            ×{item.count}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-purple-300 mt-0.5">
          {item.rarity}
        </div>
        <div className="text-[11px] text-neutral-400 leading-snug mt-1 line-clamp-3">
          {item.description}
        </div>
        <div className={`text-[10px] mt-2 italic ${canUse ? "text-emerald-300" : "text-neutral-500"}`}>
          {hint}
        </div>
        <button
          disabled={!canUse || busy}
          onClick={() => onUse(item.item_id)}
          data-testid={`nest-inv-${item.item_id}-use`}
          className={`mt-2 w-full py-1.5 font-bold uppercase tracking-widest text-[10px] transition ${
            canUse && !busy
              ? "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:brightness-125"
              : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
          }`}
        >
          {busy
            ? "Working…"
            : item.item_id === "tomb_of_awakening"
              ? (canUse ? "🪦 Awaken Streak" : "🔒 No Streak to Awaken")
              : "Use"}
        </button>
      </div>
    </div>
  );
}

